import LegalLayout from "@/components/legal/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" effectiveDate="February 13, 2026">
      <p>
        Freedom In Me (&ldquo;<strong>we</strong>&rdquo;, &ldquo;<strong>us</strong>&rdquo;) operates the Business Showers
        website at <a href="https://pitch.businessshowers.org">pitch.businessshowers.org</a> and the annual Freedom In Me
        Business Showers event held each July in Charlotte, NC. This Privacy Policy explains what information we collect,
        how we use it, and the choices you have.
      </p>
      <p>
        This policy is intentionally short and specific because we only collect what we actually use — nothing more.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>a. Information you give us directly</h3>
      <ul>
        <li><strong>Event check-in</strong>: first name, last name, email address, mobile number, business or organization (optional),
          city and state (optional), attendee category (Guest, Honoree, Sponsor, Speaker, Volunteer, etc.), and which day(s) you are attending.</li>
        <li><strong>Volunteer, sponsor, and honoree applications</strong>: contact information, organization, interests, and any notes you provide.</li>
        <li><strong>2027 Early Access</strong>: name, contact information, interest areas, and email/SMS consent selections.</li>
        <li><strong>Feedback, review, and donation clicks</strong>: we log clicks (not the payment itself) when you use our
          Google Review, donation, and social buttons so we can measure momentum.</li>
      </ul>

      <h3>b. Information we collect automatically</h3>
      <ul>
        <li><strong>Session identifier</strong>: a short random string stored in your browser session so we can count unique visits.</li>
        <li><strong>Page views &amp; interaction events</strong>: which pages you view and which buttons you tap.</li>
        <li><strong>Basic technical data</strong>: browser type, general geographic region, and time of visit.</li>
        <li><strong>Check-in cache</strong>: after you check in on-site, we store your check-in status in your device's local storage for up to 48 hours so you don't have to check in again if you close the app.</li>
      </ul>

      <p>
        We do <strong>not</strong> use behavioral advertising cookies, track you across other websites, or sell any personal information.
      </p>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To check you in to Business Showers events and confirm your attendance.</li>
        <li>To send event-day operational messages (welcome text, program link) <strong>only after you opt in on-site at check-in</strong>.</li>
        <li>To send you email updates you have specifically opted into (2027 Early Access, volunteer opportunities, sponsor updates).</li>
        <li>To recognize honorees, sponsors, and volunteers publicly (name, organization, and — with your consent — photo).</li>
        <li>To improve the event experience by understanding what content resonates.</li>
        <li>To comply with legal, tax, and nonprofit reporting requirements.</li>
      </ul>

      <h2>3. SMS &amp; Text Message Consent</h2>
      <p>
        We only send SMS to your mobile number if <strong>all three</strong> of the following are true:
      </p>
      <ol>
        <li>You checked the &ldquo;Text me event-day reminders and links&rdquo; consent box on the on-site check-in form (or the equivalent box on another opt-in form).</li>
        <li>The event administrator has SMS confirmation enabled for the current event.</li>
        <li>Your phone number is a valid US mobile number that can receive SMS.</li>
      </ol>
      <p>
        <strong>Registrants who buy a ticket in advance are not opted in for SMS.</strong> The SMS opt-in is only presented at the
        check-in kiosk on event day. This is intentional so that you decide, in the moment, whether you want event-day text messages.
      </p>
      <p>
        Message frequency: typically <strong>one text message on event day</strong>, plus an occasional follow-up
        (photo album link, thank-you) within 30 days of the event.
      </p>
      <p>
        <strong>Message and data rates may apply.</strong> Standard carrier rates from your wireless plan apply to any SMS you receive.
      </p>
      <p>
        <strong>Reply STOP</strong> to any message to opt out immediately. Reply <strong>HELP</strong> for help or contact us at
        <a href="mailto:info@itsthefreedominme.com"> info@itsthefreedominme.com</a>. Opting out of SMS does not affect your ability to attend the event
        or receive email updates you separately opted in to.
      </p>
      <p>
        SMS delivery is handled by <a href="https://www.twilio.com" target="_blank" rel="noreferrer">Twilio</a> under our
        registered A2P 10DLC campaign.
      </p>

      <h2>4. Email Communications</h2>
      <p>
        Email updates are only sent when you have specifically opted in — for example by joining the 2027 Early Access list,
        applying to volunteer, or submitting a sponsor inquiry. Every email includes a one-click unsubscribe link. Email delivery
        is handled by <a href="https://resend.com" target="_blank" rel="noreferrer">Resend</a> from
        <code>hello@fimbusinessshowers.org</code>.
      </p>

      <h2>5. Photos &amp; Video</h2>
      <p>
        Photography and video are captured at the Business Showers event and, with your consent, in group shots, ceremonies,
        and community imagery. See our <a href="/photo-release">Photo &amp; Video Release</a> for full terms and how to request that a specific image be removed.
      </p>

      <h2>6. Who Has Access to Your Information</h2>
      <ul>
        <li><strong>Freedom In Me staff, volunteers, and board members</strong> who need it to operate the event.</li>
        <li><strong>Trusted service providers</strong> we use to run the platform:
          <ul>
            <li>Twilio (SMS delivery)</li>
            <li>Resend (email delivery)</li>
            <li>MongoDB Atlas / cloud hosting (data storage)</li>
            <li>Google (administrator sign-in via OAuth)</li>
            <li>Kululu or an equivalent photo-sharing platform we designate (event photos, only if you opt to upload)</li>
          </ul>
        </li>
        <li><strong>Legal or safety requirement</strong>: we may disclose information if required by law or to protect the safety of attendees, staff, or the public.</li>
      </ul>
      <p>
        We do <strong>not</strong> rent, sell, or share your personal information with advertisers or data brokers.
      </p>

      <h2>7. How Long We Keep Your Information</h2>
      <ul>
        <li>Event check-in records: retained for up to 3 years for attendance history and grant reporting.</li>
        <li>Opt-in lists (email, SMS, early access): retained until you unsubscribe or ask to be removed.</li>
        <li>Analytics events: retained for up to 12 months in aggregate form.</li>
        <li>Financial and donation records: retained per US nonprofit tax law (typically 7 years).</li>
      </ul>

      <h2>8. Your Rights</h2>
      <p>You can, at any time:</p>
      <ul>
        <li>Request a copy of the personal information we hold about you.</li>
        <li>Ask us to correct or update your information.</li>
        <li>Ask us to delete your information (subject to legal retention rules above).</li>
        <li>Unsubscribe from email at any time using the link in any email.</li>
        <li>Opt out of SMS at any time by replying <code>STOP</code>.</li>
      </ul>
      <p>
        To exercise any of these rights, email <a href="mailto:info@itsthefreedominme.com">info@itsthefreedominme.com</a>.
      </p>

      <h2>9. Children</h2>
      <p>
        Our services are intended for adults age 18 and older. We do not knowingly collect information from children under 13.
        If you believe a child under 13 has provided information to us, please contact us and we will delete it promptly.
      </p>

      <h2>10. Security</h2>
      <p>
        We use HTTPS everywhere, hash sensitive credentials at rest, restrict admin access by allow-list, and log every administrative action to an audit trail.
        No system is perfectly secure, but we take reasonable steps consistent with the sensitivity of the information we hold.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be posted here with a new effective date.
        For material SMS-related changes we will notify existing SMS opt-in subscribers via text before the change takes effect.
      </p>

      <h2>12. Contact</h2>
      <p>
        <strong>Freedom In Me</strong><br />
        Attn: Privacy<br />
        Charlotte, NC<br />
        <a href="mailto:info@itsthefreedominme.com">info@itsthefreedominme.com</a>
      </p>
    </LegalLayout>
  );
}
